import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  isCampaignMaxClaimedTasksPerUserError,
  isCampaignMaxTasksPerUserError,
  isClaimingPausedError,
  isFirstApprovalRequiredClaimError,
} from '../../services/tasks';
import { TabDef } from '../../components/Tabs';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useViewModelReady } from '../../hooks/useViewModel';
import { DesktopTaskQueueView } from './DesktopTaskQueueView';
import { MobileTaskQueueView } from './MobileTaskQueueView';
import { buildClaimOptions } from './claimOptions';
import { ClaimOption, TabId } from './types';
import { useTaskQueueViewModel } from './viewModel';
import { createUnassignedReviewBrowseState } from '../../domain/taskQueue/unassignedReviewBrowse';
import { ReviewSortMode, sortReviewTasks } from '../../domain/taskQueue/reviewSort';
import { paths } from '../../routes/paths';

export { buildClaimOptions } from './claimOptions';
export type { ClaimOption } from './types';

export function TaskQueuePage() {
  const { isAdmin, isReviewer, reviewCampaignIds } = useAuth();
  const navigate = useNavigate();
  const vm = useTaskQueueViewModel();
  const isMobile = useIsMobile();
  useViewModelReady(vm.initialize);

  // vm.dispose is stable (useCallback with no deps); `vm` itself is a fresh
  // object per render, so only dispose belongs in this unmount cleanup.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => vm.dispose(), []);

  const [activeTab, setActiveTab] = useState<TabId>(isReviewer ? 'reviews' : 'my');
  const [claiming, setClaiming] = useState<boolean>(false);
  const [claimNotice, setClaimNotice] = useState<string | null>(null);
  const [reviewSortMode, setReviewSortMode] = useState<ReviewSortMode>('age');

  useEffect(() => {
    setClaimNotice(null);
  }, [activeTab, vm.selectedCampaignId]);

  const handleClaimNext = async (option: ClaimOption | null) => {
    setClaiming(true);
    setClaimNotice(null);
    try {
      const id = await vm.claimNext(option?.taskTypeId ?? null, option?.domainId ?? null);
      if (id) {
        setActiveTab('my');
      } else {
        setClaimNotice('No tasks available to claim right now.');
      }
    } catch (err) {
      if (isFirstApprovalRequiredClaimError(err)) {
        setClaimNotice('Your first task in this campaign must be approved before you can claim another.');
      } else if (isCampaignMaxTasksPerUserError(err)) {
        setClaimNotice('You have reached the maximum number of tasks for this campaign.');
      } else if (isCampaignMaxClaimedTasksPerUserError(err)) {
        setClaimNotice('You have reached the maximum number of claimed tasks for this campaign.');
      } else if (isClaimingPausedError(err)) {
        setClaimNotice('Claiming is paused for this queue.');
      }
    } finally {
      setClaiming(false);
    }
  };

  const handleClaimNextReview = async (option: ClaimOption | null) => {
    setClaiming(true);
    setClaimNotice(null);
    try {
      const id = await vm.claimNextReview(option?.taskTypeId ?? null, option?.domainId ?? null);
      if (id) {
        setActiveTab('reviews');
      } else {
        setClaimNotice('No reviews available to claim right now.');
      }
    } catch {
      // error surfaced via vm.error
    } finally {
      setClaiming(false);
    }
  };

  const canReviewSelected =
    isReviewer &&
    !!vm.selectedCampaignId &&
    (isAdmin || reviewCampaignIds.includes(vm.selectedCampaignId));
  const bypassClaimingPaused = isAdmin || canReviewSelected;

  const taskClaimOptions = useMemo<ClaimOption[]>(
    () => buildClaimOptions(
      vm.selectedCampaign?.taskTypes ?? [],
      vm.availableCountsByType,
      {
        respectClaimingPaused: !bypassClaimingPaused,
        maxReached: vm.selectedCampaign?.maxTasksPerUser !== undefined
          && vm.userTaskCountInSelectedCampaign >= vm.selectedCampaign.maxTasksPerUser,
        claimMaxReached: vm.selectedCampaign?.maxClaimedTasksPerUser !== undefined
          && vm.userClaimedTaskCountInSelectedCampaign >= vm.selectedCampaign.maxClaimedTasksPerUser,
      },
    ),
    [
      vm.selectedCampaign,
      vm.availableCountsByType,
      bypassClaimingPaused,
      vm.userTaskCountInSelectedCampaign,
      vm.userClaimedTaskCountInSelectedCampaign,
    ],
  );
  const reviewClaimOptions = useMemo<ClaimOption[]>(
    () => buildClaimOptions(
      vm.selectedCampaign?.taskTypes ?? [],
      vm.awaitingReviewCountsByType,
      { respectClaimingPaused: false },
    ),
    [vm.selectedCampaign, vm.awaitingReviewCountsByType],
  );
  const unassignedReviewBrowseState = useMemo(
    () => createUnassignedReviewBrowseState(
      vm.selectedCampaignId ?? '',
      vm.buckets.awaitingReview,
    ),
    [vm.buckets.awaitingReview, vm.selectedCampaignId],
  );
  const handleBrowseUnassigned = () => {
    navigate(
      paths.task(unassignedReviewBrowseState.taskIds[0]!),
      { state: unassignedReviewBrowseState },
    );
  };
  const sortedBuckets = useMemo(
    () => ({
      ...vm.buckets,
      myReviews: sortReviewTasks(vm.buckets.myReviews, reviewSortMode),
    }),
    [vm.buckets, reviewSortMode],
  );
  const viewModelForView = useMemo(
    () => ({ ...vm, buckets: sortedBuckets }),
    [vm, sortedBuckets],
  );

  const myTasksCount = sortedBuckets.myActive.length;
  const tabs: TabDef[] = [
    { id: 'my', label: 'My Tasks', count: myTasksCount },
    { id: 'rework', label: 'Rework', count: sortedBuckets.changesRequested.length },
    { id: 'submitted', label: 'Submitted', count: sortedBuckets.submitted.length },
  ];
  if (isReviewer) {
    tabs.unshift({ id: 'reviews', label: 'Review Queue', count: sortedBuckets.myReviews.length });
  }
  // Approved tab is visible only to reviewers on the selected campaign and to
  // admins. The viewmodel gates the per-campaign subscription on the same
  // condition, so for writers `approvedInCampaign` is always empty and the
  // tab would just show "Nothing here" — hide it instead.
  if (isAdmin || canReviewSelected) {
    tabs.push({ id: 'approved', label: 'Approved', count: sortedBuckets.approvedInCampaign.length });
  }
  const effectiveTab: TabId = tabs.some((tab) => tab.id === activeTab) ? activeTab : 'my';
  const props = {
    vm: viewModelForView,
    tabs,
    effectiveTab,
    isReviewer,
    canReviewSelected,
    claiming,
    claimNotice,
    taskClaimOptions,
    reviewClaimOptions,
    unassignedReviewCount: unassignedReviewBrowseState.taskIds.length,
    reviewSortMode,
    onTabChange: setActiveTab,
    onReviewSortChange: setReviewSortMode,
    onClaimNext: handleClaimNext,
    onClaimNextReview: handleClaimNextReview,
    onBrowseUnassigned: handleBrowseUnassigned,
  };

  return isMobile
    ? <MobileTaskQueueView {...props} />
    : <DesktopTaskQueueView {...props} />;
}
